import React from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';

const ACCENT = '#137274';

const Node = ({ label, accent, wide }) => (
  <Box
    sx={{
      px: wide ? 2 : 1.25,
      py: 0.85,
      borderRadius: 1.5,
      border: `1.5px solid ${accent}`,
      bgcolor: alpha(accent, 0.12),
      color: accent,
      fontWeight: 800,
      fontSize: '0.72rem',
      textAlign: 'center',
      minWidth: wide ? 88 : 64,
      lineHeight: 1.25,
    }}
  >
    {label}
  </Box>
);

const Arrow = ({ label, muted }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 0.5, color: muted }}>
    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700 }}>{label || ''}</Typography>
    <Typography sx={{ fontSize: '1.1rem', lineHeight: 1 }}>→</Typography>
  </Box>
);

const Frame = ({ children, caption }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? '#30363d' : 'rgba(15,60,70,0.12)';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  return (
    <Box
      sx={{
        my: 2,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${border}`,
        bgcolor: isDark ? alpha('#fff', 0.03) : alpha(ACCENT, 0.03),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          rowGap: 1.25,
        }}
      >
        {children}
      </Box>
      {caption && (
        <Typography
          sx={{ mt: 1.5, textAlign: 'center', fontSize: '0.78rem', color: muted, lineHeight: 1.45 }}
        >
          {caption}
        </Typography>
      )}
    </Box>
  );
};

/** Lightweight visual diagrams for interview-prep articles. */
const TopicDiagram = ({ kind, caption }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const blue = '#2563eb';
  const pink = '#f50057';
  const amber = '#d97706';

  switch (kind) {
    case 'scale-vertical-horizontal':
      return (
        <Frame caption={caption}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: muted, mb: 0.75 }}>
              VERTICAL
            </Typography>
            <Node label="Bigger server" accent={amber} wide />
          </Box>
          <Arrow muted={muted} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: muted, mb: 0.75 }}>
              HORIZONTAL
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              <Node label="App 1" accent={ACCENT} />
              <Node label="App 2" accent={ACCENT} />
              <Node label="App 3" accent={ACCENT} />
            </Box>
          </Box>
        </Frame>
      );

    case 'cap-triangle':
      return (
        <Frame caption={caption}>
          <Box sx={{ position: 'relative', width: 220, height: 150 }}>
            <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)' }}>
              <Node label="Consistency" accent={blue} />
            </Box>
            <Box sx={{ position: 'absolute', bottom: 0, left: 0 }}>
              <Node label="Availability" accent={ACCENT} />
            </Box>
            <Box sx={{ position: 'absolute', bottom: 0, right: 0 }}>
              <Node label="Partition" accent={pink} />
            </Box>
            <Typography
              sx={{
                position: 'absolute',
                top: '42%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '0.7rem',
                fontWeight: 800,
                color: muted,
              }}
            >
              pick 2 during split
            </Typography>
          </Box>
        </Frame>
      );

    case 'load-balancer':
    case 'api-gateway':
      return (
        <Frame caption={caption}>
          <Node label="Clients" accent={muted} />
          <Arrow muted={muted} />
          <Node
            label={kind === 'api-gateway' ? 'API Gateway' : 'Load Balancer'}
            accent={blue}
            wide
          />
          <Arrow muted={muted} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            <Node label="Service A" accent={ACCENT} />
            <Node label="Service B" accent={ACCENT} />
            <Node label="Service C" accent={ACCENT} />
          </Box>
        </Frame>
      );

    case 'cache-aside':
      return (
        <Frame caption={caption}>
          <Node label="App" accent={blue} />
          <Arrow label="1. get" muted={muted} />
          <Node label="Cache" accent={amber} />
          <Arrow label="miss" muted={muted} />
          <Node label="Database" accent={ACCENT} wide />
        </Frame>
      );

    case 'replication':
      return (
        <Frame caption={caption}>
          <Node label="Leader (writes)" accent={pink} wide />
          <Arrow label="replicate" muted={muted} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            <Node label="Follower" accent={ACCENT} />
            <Node label="Follower" accent={ACCENT} />
          </Box>
        </Frame>
      );

    case 'sharding':
      return (
        <Frame caption={caption}>
          <Node label="App / Router" accent={blue} wide />
          <Arrow muted={muted} />
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <Node label="Shard 0" accent={ACCENT} />
            <Node label="Shard 1" accent={ACCENT} />
            <Node label="Shard 2" accent={ACCENT} />
          </Box>
        </Frame>
      );

    case 'cdn':
      return (
        <Frame caption={caption}>
          <Node label="User" accent={muted} />
          <Arrow muted={muted} />
          <Node label="Edge PoP" accent={amber} />
          <Arrow label="miss" muted={muted} />
          <Node label="Origin" accent={ACCENT} />
        </Frame>
      );

    case 'queue':
      return (
        <Frame caption={caption}>
          <Node label="Producer" accent={blue} />
          <Arrow muted={muted} />
          <Node label="Queue / Topic" accent={amber} wide />
          <Arrow muted={muted} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            <Node label="Worker 1" accent={ACCENT} />
            <Node label="Worker 2" accent={ACCENT} />
          </Box>
        </Frame>
      );

    case 'url-shortener':
      return (
        <Frame caption={caption}>
          <Node label="Write API" accent={blue} />
          <Arrow muted={muted} />
          <Node label="ID / Hash" accent={amber} />
          <Arrow muted={muted} />
          <Node label="DB + Cache" accent={ACCENT} wide />
          <Arrow label="read 302" muted={muted} />
          <Node label="Redirect" accent={pink} />
        </Frame>
      );

    case 'news-feed':
      return (
        <Frame caption={caption}>
          <Node label="Post write" accent={pink} />
          <Arrow label="fan-out" muted={muted} />
          <Node label="Feed cache" accent={amber} wide />
          <Arrow muted={muted} />
          <Node label="Read API" accent={ACCENT} />
        </Frame>
      );

    case 'chat':
      return (
        <Frame caption={caption}>
          <Node label="Client WS" accent={muted} />
          <Arrow muted={muted} />
          <Node label="Chat server" accent={blue} wide />
          <Arrow muted={muted} />
          <Node label="Session map" accent={amber} />
          <Arrow muted={muted} />
          <Node label="Inbox DB" accent={ACCENT} />
        </Frame>
      );

    case 'solid-principles':
      return (
        <Frame caption={caption || 'SOLID — five design habits that keep code changeable.'}>
          {['S', 'O', 'L', 'I', 'D'].map((letter, i) => (
            <Node
              key={letter}
              label={letter}
              accent={[ACCENT, blue, amber, pink, '#7c3aed'][i]}
            />
          ))}
        </Frame>
      );

    case 'class-diagram':
      return (
        <Frame caption={caption || 'Typical LLD shape: interface → implementations → collaborators.'}>
          <Node label="Interface / Abstract" accent={blue} wide />
          <Arrow muted={muted} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            <Node label="Impl A" accent={ACCENT} />
            <Node label="Impl B" accent={ACCENT} />
          </Box>
          <Arrow label="uses" muted={muted} />
          <Node label="Dependency" accent={amber} />
        </Frame>
      );

    case 'pattern-factory':
      return (
        <Frame caption={caption || 'Client asks Factory for a Product — never new ConcreteX() directly.'}>
          <Node label="Client" accent={muted} />
          <Arrow muted={muted} />
          <Node label="Factory" accent={blue} />
          <Arrow muted={muted} />
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <Node label="Product A" accent={ACCENT} />
            <Node label="Product B" accent={ACCENT} />
          </Box>
        </Frame>
      );

    case 'pattern-observer':
      return (
        <Frame caption={caption || 'Subject notifies all Observers when state changes.'}>
          <Node label="Subject" accent={blue} />
          <Arrow label="notify" muted={muted} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            <Node label="Observer 1" accent={ACCENT} />
            <Node label="Observer 2" accent={ACCENT} />
            <Node label="Observer 3" accent={ACCENT} />
          </Box>
        </Frame>
      );

    case 'pattern-strategy':
      return (
        <Frame caption={caption || 'Context holds a Strategy; swap algorithms without rewriting callers.'}>
          <Node label="Context" accent={blue} />
          <Arrow muted={muted} />
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <Node label="Strategy A" accent={ACCENT} />
            <Node label="Strategy B" accent={amber} />
          </Box>
        </Frame>
      );

    case 'two-phase-commit':
      return (
        <Frame caption={caption}>
          <Node label="Coordinator" accent={blue} wide />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, alignItems: 'center' }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: muted }}>1. prepare?</Typography>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: muted }}>2. commit!</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            <Node label="Participant A" accent={ACCENT} />
            <Node label="Participant B" accent={ACCENT} />
            <Node label="Participant C" accent={ACCENT} />
          </Box>
        </Frame>
      );

    case 'gossip':
      return (
        <Frame caption={caption}>
          <Box sx={{ position: 'relative', width: 240, height: 160 }}>
            {[
              { top: '4%', left: '46%', accent: pink, label: 'Node A' },
              { top: '38%', left: '4%', accent: ACCENT, label: 'Node B' },
              { top: '38%', left: '80%', accent: ACCENT, label: 'Node C' },
              { top: '78%', left: '20%', accent: muted, label: 'Node D' },
              { top: '78%', left: '62%', accent: muted, label: 'Node E' },
            ].map((n) => (
              <Box key={n.label} sx={{ position: 'absolute', top: n.top, left: n.left }}>
                <Node label={n.label} accent={n.accent} />
              </Box>
            ))}
          </Box>
        </Frame>
      );

    case 'service-discovery':
      return (
        <Frame caption={caption}>
          <Node label="Client" accent={muted} />
          <Arrow label="lookup" muted={muted} />
          <Node label="Registry" accent={blue} wide />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            <Node label="Instance 1" accent={ACCENT} />
            <Node label="Instance 2" accent={ACCENT} />
          </Box>
        </Frame>
      );

    case 'geo-search':
      return (
        <Frame caption={caption}>
          <Node label="Query point" accent={pink} />
          <Arrow label="resolve cells" muted={muted} />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Node label="Cell NW" accent={muted} />
            <Node label="Cell N" accent={ACCENT} />
            <Node label="Cell NE" accent={muted} />
          </Box>
          <Arrow label="rank" muted={muted} />
          <Node label="Nearby results" accent={blue} wide />
        </Frame>
      );

    case 'parking-lot':
    case 'elevator':
      return (
        <Frame
          caption={
            caption ||
            (kind === 'elevator'
              ? 'Controller assigns elevators; each car has state and a queue of requests.'
              : 'Entrance gates → parking manager → spots/floors; tickets track occupancy.')
          }
        >
          <Node label={kind === 'elevator' ? 'Request' : 'Vehicle'} accent={muted} />
          <Arrow muted={muted} />
          <Node label="Controller" accent={blue} wide />
          <Arrow muted={muted} />
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <Node label={kind === 'elevator' ? 'Car A' : 'Floor 1'} accent={ACCENT} />
            <Node label={kind === 'elevator' ? 'Car B' : 'Floor 2'} accent={ACCENT} />
          </Box>
        </Frame>
      );

    default:
      return (
        <Frame caption={caption || `Diagram: ${kind}`}>
          <Node label="Input" accent={muted} />
          <Arrow muted={muted} />
          <Node label="System" accent={ACCENT} wide />
          <Arrow muted={muted} />
          <Node label="Output" accent={blue} />
        </Frame>
      );
  }
};

export default TopicDiagram;
